import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Grid, Loader, Table } from "semantic-ui-react";

import { MdClose, MdModeEditOutline, MdSave } from "react-icons/md";
import { useGetTransportTypesList } from "../../../../api/static-data";
import { DropDown, InputText, TitleView } from "../../../../components";
import { ENABLE_EXTERNAL_TRANSPORT_TOOL, WEIGHT_UNIT_TYPES_OPTIONs } from "../../../../config/constants";
import { InputNumber } from "../../../../components/InputText/InputNumber";
import InputWithDropDown from "../../../../components/inputWithDropDown/InputWithDropDown";
import { checkValueIsEmpty } from "../../../../utils/utils";
import { formatFreightWorkTkm } from "../../UpdateNodes/shared/internalTransport/internalTransport.utils";

import { usePutSimulationFarmGateInternalTransportData } from "../../../../api/cultivations";
import { usePutSimulationProcessGateInternalTransportData } from "../../../../api/simulation/process";
import "./ProcessFarmGateTransport.scss";

const ProcessFarmGateTransport = ({ reviewData, refetch, isEditable }: any) => {
  const { data: transportationTypesData } = useGetTransportTypesList();
  const [outputUnitTypes, setOutputUnitTypes] = useState<any>(WEIGHT_UNIT_TYPES_OPTIONs[0]);
  const [selectTransport, setSelectTransport] = useState<any>(null);
  const [transportTypeDefault, setDefaultTransportType] = useState<any>(null);
  const { mutate: putInternalTransportDataMutate, isLoading: isProcessGateLoading } =
    usePutSimulationProcessGateInternalTransportData();
  const { mutate: putInternalFarmTransportDataMutate, isLoading: isFarmGateLoading } =
    usePutSimulationFarmGateInternalTransportData();

  const trasportData = useCallback((currentData: any[], packageStatus: string) => {
    return (
      currentData
        ?.map((transport: any) => {
          const name = transport?.[packageStatus];
          const transportData = transport?.internalTransportation || [];

          if (!transportData.length) return null;

          return {
            gateId: transport?._id,
            processOrFarmGateName: name,
            gateType: packageStatus === "processGateName" ? "Process Gate" : "Farm Gate",
            transportData
          };
        })
        .filter(Boolean) || []
    );
  }, []);

  const processFarGateTransportData = useMemo(() => {
    return [
      ...trasportData(reviewData?.processGate || [], "processGateName"),
      ...trasportData(reviewData?.farmGate || [], "farmGateName")
    ];
  }, [reviewData, trasportData]);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
    setValue
  } = useForm<any>({
    defaultValues: {
      processFarGateTransportData
    }
  });

  const editData = (row: any) => {
    setSelectTransport(row);
    reset({
      processFarGateTransportData: processFarGateTransportData.map((gate: any) => ({
        ...gate,
        transportData: gate.transportData.map((t: any) =>
          t._id === row._id
            ? {
                ...t,
                weight: t.weight,
                distance: t.distance
              }
            : t
        )
      }))
    });
    setOutputUnitTypes(row?.weightUnit);
    setDefaultTransportType(row?.transportationType?.toString() || null);
  };

  const onSubmit = (data: any) => {
    let result = data.processFarGateTransportData.reduce((acc: any, gate: any) => {
      const updatedTransport = gate.transportData.find((t: any) => t._id === selectTransport?._id);

      if (updatedTransport) {
        return {
          processGateId: gate.gateId,
          farmGateId: gate.gateId,
          gateType: gate.gateType,
          internalTransportId: updatedTransport._id,
          transportData: updatedTransport
        };
      }

      return acc;
    }, null);

    if (result?.gateType === "Process Gate") {
      putInternalTransportDataMutate(result, {
        onSuccess: () => {
          refetch();
          setSelectTransport(null);
        }
      });
    } else {
      putInternalFarmTransportDataMutate(result, {
        onSuccess: () => {
          refetch();
          setSelectTransport(null);
        }
      });
    }
  };

  const handleCancel = () => {
    setSelectTransport(null);
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TitleView CustomTitleViewMain="mt-2" title="Transportation (Process and Farm Gate)" />

      <Table celled className="table" structured>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell className="tableHeader" colSpan="1" textAlign="center">
              Transportation (Process and Farm gate)
            </Table.HeaderCell>
            <Table.HeaderCell
              className="tableHeader"
              colSpan="15"
              textAlign="center"
              style={{ backgroundColor: "#f4c6db", color: "#000" }}
            >
              Input field
            </Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell className="tableHeaderReview">Gate</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Transportation Name</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Transportation Type</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Weight</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Distance (km)</Table.HeaderCell>
            {ENABLE_EXTERNAL_TRANSPORT_TOOL && (
              <>
                <Table.HeaderCell className="tableHeaderReview">Freight work (t·km)</Table.HeaderCell>
                <Table.HeaderCell className="tableHeaderReview">Vehicle</Table.HeaderCell>
              </>
            )}
            <Table.HeaderCell className="tableHeaderReview"></Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {processFarGateTransportData.map((gate: any, gateIndex: number) => {
            const rowSpan = gate.transportData.length;

            return gate.transportData.map((row: any, rowIndex: number) => {
              const weightUnit = WEIGHT_UNIT_TYPES_OPTIONs.find(
                (e: any) => e.key === row.weightUnit
              );
              const isSelectTransport = row?._id === selectTransport?._id;

              const transportType = transportationTypesData?.find(
                (t: any) => t.value == row.transportationType
              );

              return (
                <Table.Row key={`${gateIndex}-${rowIndex}`}>
                  {rowIndex === 0 && (
                    <Table.Cell rowSpan={rowSpan}>
                      {gate.processOrFarmGateName} ({gate.gateType})
                    </Table.Cell>
                  )}

                  <Table.Cell className={checkValueIsEmpty(row.internalTransportName)}>
                    {isSelectTransport ? (
                      <InputText
                        register={register}
                        errors={errors.internalTransportName}
                        placeholder="Internal Transport Name"
                        name={`processFarGateTransportData.${gateIndex}.transportData.${rowIndex}.internalTransportName`}
                        required={true}
                        errorMessage={"Please enter Internal Transport Name"}
                      />
                    ) : (
                      <> {row.internalTransportName || "Missing name"}</>
                    )}
                  </Table.Cell>

                  <Table.Cell className="customTransportDropDown">
                    {isSelectTransport ? (
                      <DropDown
                        required={true}
                        placeholder="Select Transportation Type"
                        upward={true}
                        currentData={transportationTypesData}
                        defaultValue={transportTypeDefault}
                        customGridColumn={"customGridColomnType"}
                        handleChangeState={(e: any, { value }: any) => {
                          setDefaultTransportType(value);
                          setValue(
                            `processFarGateTransportData.${gateIndex}.transportData.${rowIndex}.transportationType`,
                            value,
                            { shouldDirty: true, shouldValidate: true }
                          );
                        }}
                      />
                    ) : (
                      <>{transportType?.text}</>
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    {isSelectTransport ? (
                      <InputWithDropDown
                        defaultSelectValue={outputUnitTypes}
                        options={WEIGHT_UNIT_TYPES_OPTIONs}
                        selectOption={(value: string | number) => {
                          setOutputUnitTypes(value);
                          setValue(
                            `processFarGateTransportData.${gateIndex}.transportData.${rowIndex}.weightUnit`,
                            value,
                            { shouldDirty: true, shouldValidate: true }
                          );
                        }}
                        control={control}
                        placeholder="weight"
                        name={`processFarGateTransportData.${gateIndex}.transportData.${rowIndex}.weight`}
                        upward
                        errors={
                          (errors?.processFarGateTransportData as any)?.[gateIndex]
                            ?.transportData?.[rowIndex]?.weight
                        }
                        errorMessage="Weight is required"
                        customText="customProcessReviewInput"
                        valueAsNumber={true}
                        required={true}
                        transform={{
                          input: (value: any) =>
                            isNaN(value) || value === 0 ? "" : value.toString(),
                          output: (e: any) => {
                            return e;
                          }
                        }}
                      />
                    ) : (
                      <>
                        {row.weight} {weightUnit?.text}
                      </>
                    )}
                  </Table.Cell>

                  <Table.Cell className={checkValueIsEmpty(row.averageDistance)}>
                    {isSelectTransport ? (
                      <InputNumber
                        control={control}
                        placeholder="Distance"
                        name={`processFarGateTransportData.${gateIndex}.transportData.${rowIndex}.averageDistance`}
                        transform={{
                          input: (v: any) =>
                            v === null || v === undefined || isNaN(v) ? "" : v.toString(),
                          output: (e: any) => e
                        }}
                        errors={
                          (errors?.processFarGateTransportData as any)?.[gateIndex]
                            ?.transportData?.[rowIndex]?.distance
                        }
                        required
                        errorMessage="Distance is required"
                      />
                    ) : (
                      <>{row.averageDistance || 0}</>
                    )}
                  </Table.Cell>
                  {ENABLE_EXTERNAL_TRANSPORT_TOOL && (
                    <>
                      <Table.Cell>
                        {formatFreightWorkTkm(
                          row.weight,
                          row.weightUnit,
                          row.averageDistance,
                          row.freightWorkTkm ?? row.freight_work_tkm
                        )}
                      </Table.Cell>
                      <Table.Cell>{transportType?.text || "—"}</Table.Cell>
                    </>
                  )}
                  <Table.Cell>
                    <Grid>
                      <Grid.Column computer={4} tablet={5} mobile={5}>
                        {(isProcessGateLoading || isFarmGateLoading) && isSelectTransport ? (
                          <Loader active size="small" inline="centered" />
                        ) : isSelectTransport ? (
                          <MdSave
                            cursor="pointer"
                            size={24}
                            color="var(--tableEditIcon)"
                            title="Save"
                            onClick={handleSubmit(onSubmit)}
                          />
                        ) : (
                          <>
                            {isEditable && (
                              <MdModeEditOutline
                                cursor="pointer"
                                size={24}
                                color="var(--tableEditIcon)"
                                onClick={() => editData(row)}
                              />
                            )}
                          </>
                        )}
                      </Grid.Column>
                      {!(isProcessGateLoading || isFarmGateLoading) && isSelectTransport ? (
                        <Grid.Column computer={4} tablet={16} mobile={16}>
                          <MdClose
                            cursor={"pointer"}
                            size={24}
                            color="var(--tableEditIcon)"
                            onClick={handleCancel}
                          />
                        </Grid.Column>
                      ) : null}
                    </Grid>
                  </Table.Cell>
                </Table.Row>
              );
            });
          })}
        </Table.Body>
      </Table>
    </form>
  );
};

export default ProcessFarmGateTransport;
